import S from 's-js';
import SArray, { SArray as ArraySignal } from 's-array';
import * as Promise from 'bluebird';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { IAjax, Client } from 'lib/Ajax';
import { DraftObject } from 'lib/draftSignals';
import { AutoSavingDraftObject, AutoSaver } from 'lib/autoSave';
import { ValidationWatcher } from 'lib/validationWatcher';
import { PCCache } from 'data/PCCache';
import { BerthAmenityType } from 'data/models/BerthAmenityType';
import { Organization, OrganizationOptions } from 'data/models/Organization';
import { TopicCardQuestionType } from 'server/enums/TopicCardQuestionType';
import { TopicCardTemplate as ServerTopicCardTemplate, TopicCardTemplateOptions } from 'data/models/TopicCardTemplate';
import { TopicCardTemplateAttachment as ServerTopicCardTemplateAttachment, TopicCardTemplateAttachmentOptions } from 'data/models/TopicCardTemplateAttachment';
import { TopicCardTemplateOption, TopicCardTemplateOptionOptions } from 'data/models/TopicCardTemplateOption';
import { TopicCardTemplateUserAssignment as ServerTopicCardTemplateUserAssignment, TopicCardTemplateUserAssignmentData } from 'data/models/TopicCardTemplateUserAssignment';
import { UploadFileResponseData } from 'data/models/UploadFileResponse';
import { UserSelector } from 'portcall/userselector/model';
import { UploadFileParams, UploadFile } from 'portcall/uploadfile/model';

export interface TopicCardTemplatesParams {
    id : number;
    templateId : number;
}

export function TopicCardTemplates(
    loader : CachedModelLoader<Organization, OrganizationOptions>,
    ajax : IAjax,
    data : PCCache,
    uploadFileModel : (params : UploadFileParams<UploadFileResponseData>) => UploadFile,
    { id, templateId } : TopicCardTemplatesParams
) {
    const { model, loading } = loader({ Id: id }, 30),
        templatesClient = ajax.Url<TopicCardTemplateOptions>(() => model.TopicCardTemplatesUrl() || ''),
        selectedTemplateId = S.data(templateId),
        amenityTypes = model.BerthAmenityTypes.sort((a1, a2) => a1.Order() - a2.Order()),
        templates = model.TopicCardTemplates
            .map(t => TopicCardTemplate(ajax, data, amenityTypes, uploadFileModel, () => selectedTemplateId(0), t, loading))
            .filter(t => t.Model.Active())
            .sort((t1, t2) =>
                t1.Model.EffectiveTitle().toLowerCase() < t2.Model.EffectiveTitle().toLowerCase() ? -1
              : t1.Model.EffectiveTitle().toLowerCase() > t2.Model.EffectiveTitle().toLowerCase() ? 1
              : 0),
        selectedTemplate = templates.find(t => t.Model.Id() === selectedTemplateId());

    return {
        Id: id,
        Model: model,
        TopicCardTemplates: templates,
        SelectedTemplateId: selectedTemplateId,
        SelectedTemplate: selectedTemplate,
        AddTemplate
    };

    function AddTemplate() {
        let req = templatesClient.Post({
            Id: 0,
            Active: true,
            Question: '--',
            QuestionType: TopicCardQuestionType.YesNo,
            Title: '--'
        });
        req.then(json => {
            S.freeze(() => {
                data.TopicCardTemplates.update1(req.Date, json);
                selectedTemplateId(json.Id);
            });
        });
    }
}
export type TopicCardTemplates = ReturnType<typeof TopicCardTemplates>;


function TopicCardTemplate(
    ajax : IAjax,
    data : PCCache,
    amenityTypes : ArraySignal<BerthAmenityType>,
    uploadFileModel : (params : UploadFileParams<UploadFileResponseData>) => UploadFile,
    deselectTemplate : () => void,
    template : ServerTopicCardTemplate,
    loading : () => boolean
) {
    const client = ajax.Url<ServerTopicCardTemplate>(() => template.Url() || ''),
        validation = ValidationWatcher(client),
        draft = AutoSavingDraftObject(template, client, [
            'AmenityTypeId',
            'Active',
            'Question',
            'QuestionType',
            'Remarks',
            'TimeEntry.HasTimeEntry',
            'TimeEntry.Label',
            'Title'
        ]),
        amenityLink = TopicCardTemplateAmenityLink(amenityTypes, draft),
        options = TopicCardTemplateOptionsSection(ajax, data, template, loading),
        people = TopicCardTemplatePeopleSection(ajax, data, template),
        attachments = TopicCardTemplateAttachmentsSection(ajax, data, uploadFileModel, template);

    draft.AmenityType = S.value(S.sample(template.AmenityType));
    data.TopicCardTemplates.attach(draft, [draft.AmenityType]);

    return {
        Model: draft,
        Validation: validation,
        AmenityLink: amenityLink,
        Options: options,
        People: people,
        Attachments: attachments,
        Delete
    };

    function Delete() {
        S.freeze(() => {
            draft.Active(false);
            deselectTemplate();
        });
    }
}
export type TopicCardTemplate = ReturnType<typeof TopicCardTemplate>;

function TopicCardTemplateAmenityLink (
    amenityTypes : ArraySignal<BerthAmenityType>,
    draft : ServerTopicCardTemplate
) {
    const showDropdown = S.data(S.sample(draft.AmenityTypeId) !== null),
        availableAmenityTypes = amenityTypes.filter(a => a.Active() || a.Id() === draft.AmenityTypeId());
    
    return {
        ShowDropdown: showDropdown,
        AvailableAmenityTypes: availableAmenityTypes,
        UndoAmenity
    };

    function UndoAmenity() {
        S.freeze(() => {
            draft.AmenityTypeId(null);
            showDropdown(false);
        });
    }
}

// Minimum number of slots shown for a template
const MIN_SLOTS = 3;

interface OptionSlotParams {
    option : TopicCardTemplateOption;
    persisted : boolean;
}

function TopicCardTemplateOptionsSection(
    ajax : IAjax,
    data : PCCache,
    template : ServerTopicCardTemplate,
    loading : () => boolean // Is the page loading?
) {
    const addOptionsClient = ajax.Url<TopicCardTemplateOption, TopicCardTemplateOptionOptions>(() => template.OptionsUrl() || ''),
        slotParams = SArray<OptionSlotParams>([]),
        slots = slotParams.map(params => TopicCardTemplateOptionSlot(ajax, data, addOptionsClient, params));

    S(() => {
        // TH 12/07/2016 -- Switch between two different sources of updates for our slotParams.
        // While the page is loading, slotParams updates live from template.Options.
        // That way we get the latest options from the server.
        // After we're done loading, stop updating from template.Options, and instead switch on
        // logic for adding empty slots, that way users can start adding new options.
        // NOTE: This depends on the fact that the CachedModelLoader updates the loading signal
        // after an async delay from when it updates the cache.
        // So we'll always get a moment of time where template.Options is up-to-date and loading is still true.
        if (loading()) {
            const liveOptions = template.Options
                .sort((o1, o2) => o1.DisplayOrder() - o2.DisplayOrder())
                .map(o => ({ option: o, persisted : true }) as OptionSlotParams);
            S(() => slotParams(liveOptions()));
        } else {
            const lastSlot = slots().length > 0 ? slots()[slots().length - 1] : undefined;
            let displayOrder = lastSlot ? lastSlot.Model.DisplayOrder() + 1 : 0;
            for (let i = slots().length; i < MIN_SLOTS || lastSlot && lastSlot.IsPersisted() && i < slots().length + 1; i++) {
                slotParams.push({
                    option: new TopicCardTemplateOption({ Id: 0, DisplayOrder: displayOrder++ }),
                    persisted: false
                });
            }
        }
    });


    return {
        Slots: slots,
        DeleteSlot
    };

    function DeleteSlot(_slot : TopicCardTemplateOptionSlot) {
        _slot.Delete(() => slotParams.remove(_slot.Params));
    }
}

let slotId = 0;

function TopicCardTemplateOptionSlot(
    ajax : IAjax,
    data : PCCache,
    addClient : Client<TopicCardTemplateOption, TopicCardTemplateOptionOptions>,
    params : OptionSlotParams
) {
    // TH 11/30/2016 -- Support UX where typing into the text box upserts the option and clearing the text box deletes it.
    // All w/ a debounced save (via autoSave.ts).
    const { option, persisted: initialPersisted } = params,
        isPersisted = S.data(initialPersisted),
        client = ajax.Url<TopicCardTemplateOption, TopicCardTemplateOptionOptions>(() => option.Url() || ''),
        validation = ValidationWatcher(client),
        { object: draft, methods: draftMethods} = DraftObject(option);

    AutoSaver(draftMethods.onWrite, Save);

    return {
        // TH -- Give each slot a unique id for React keying purposes
        SlotId: slotId++,
        Params: params,
        Model: draft,
        Validation: validation,
        IsPersisted: isPersisted,
        Delete
    };

    function Save() {
        var checkpoint = draftMethods.checkpoint();
        // "Saving" an unpersisted option w/ no Text is a noop
        if (!isPersisted() && !draft.Text()) {
            checkpoint.revert();
            return Promise.resolve();
        }
        if (isPersisted()) {
            return client.Put(draft).then(() => {
                checkpoint.commit();
            });
        } else {
            let req = addClient.Post(draft);
            return req.then(json => S.freeze(() => {
                isPersisted(true);
                // TH 12/01/2016 -- NOTE Passing the unpersisted option object as the template param.
                data.TopicCardTemplateOptions.update1(req.Date, json, option);
                checkpoint.revert();
            }));
        }
    }

    function Delete(removeSlot : () => void) {
        const id = option.Id(),
            req = client.Delete();
        req.then(() => S.freeze(() => {
            data.TopicCardTemplateOptions.update(req.Date, [], [id]);
            removeSlot();
        }));
    }
}
export type TopicCardTemplateOptionSlot = ReturnType<typeof TopicCardTemplateOptionSlot>;

function TopicCardTemplatePeopleSection(
    ajax : IAjax,
    data : PCCache,
    template : ServerTopicCardTemplate
) {
    const availableUsers = S(() => Array.from(data.PCUsers.items())
            .filter(u => {
                const assignmentKey = ServerTopicCardTemplateUserAssignment.dataKey({ TemplateId: template.Id(), UserId: u.Id() });
                // Only active users who aren't assigned to this template
                return u.Active() && !data.TopicCardTemplateUserAssignments.index(assignmentKey)();
            })),
        selectedUserId = S.value(0),
        userSelector = UserSelector({
            Users: availableUsers,
            UserId: selectedUserId
        });

    let lastRequest = Promise.resolve();

    S(() => {
        if (selectedUserId()) {
            const url = (template.PeopleUrlBase() || '').replace('{userId}', '' + selectedUserId()),
                client = ajax.Url<{}, TopicCardTemplateUserAssignmentData>(url);

            lastRequest.finally(() => {
                let req = client.Put({});
                lastRequest = req.then(json => {
                    data.TopicCardTemplateUserAssignments.update1(req.Date, json);
                });
            });

            // Clear typeahead value
            userSelector.data('');
        }
    });

    return {
        People: template.People
            .filter(ua => !!ua.User() && ua.User()!.Active())
            .sort((ua1, ua2) =>
                ua1.User() ?
                    ua2.User() ?
                        ua1.User()!.LastName().toLowerCase() < ua2.User()!.LastName().toLowerCase() ?
                            -1 :
                            ua1.User()!.LastName().toLowerCase() > ua2.User()!.LastName().toLowerCase() ?
                                1 :
                                0 :
                        -1 : // sort unpopulated users last
                    ua2.User() ?
                        1 :
                        0
            )
            .map(p => TopicCardTemplateUserAssignment(ajax, data, p)),
        UserSelector: userSelector
    };
}

function TopicCardTemplateUserAssignment(
    ajax : IAjax,
    data : PCCache,
    assignment : ServerTopicCardTemplateUserAssignment
) {
    const client = ajax.Url(() => assignment.Url() || '');

    return {
        Model: assignment,
        Delete
    };

    function Delete() {
        const id = ServerTopicCardTemplateUserAssignment.key(assignment),
            req = client.Delete();
        req.then(() => {
            data.TopicCardTemplateUserAssignments.update(req.Date, [], [id]);
        });
    }
}
export type TopicCardTemplateUserAssignment = ReturnType<typeof TopicCardTemplateUserAssignment>;

function TopicCardTemplateAttachmentsSection(
    ajax : IAjax,
    data : PCCache,
    uploadFileModel : (params : UploadFileParams<UploadFileResponseData>) => UploadFile,
    template : ServerTopicCardTemplate
) {
    const client = ajax.Url<TopicCardTemplateAttachmentOptions>(() => template.AttachmentsUrl() || ''),
        saving = S.data(false),
        uploadFile = uploadFileModel({
            onComplete: ({ Url }) => {
                saving(true);
                let req = client.Post({
                    Id: 0,
                    AttachmentUrl: Url || '',
                    DisplayOrder: NextDisplayOrder()
                });
                req.then(json => {
                    data.TopicCardTemplateAttachments.update1(req.Date, json);
                }).finally(() => saving(false));
            }
        });

    return {
        Attachments: template.Attachments
            .sort((a1, a2) => a1.DisplayOrder() - a2.DisplayOrder())
            .map(a => TopicCardTemplateAttachment(ajax, data, a)),
        Saving: saving,
        UploadFile: uploadFile
    };

    function NextDisplayOrder() {
        return template.Attachments().length > 0 ?
            Math.max(...template.Attachments().map(a => a.DisplayOrder())) + 1 : 0;
    }
}

function TopicCardTemplateAttachment(
    ajax : IAjax,
    data : PCCache,
    attachment : ServerTopicCardTemplateAttachment
) {
    const client = ajax.Url(() => attachment.Url() || '');

    return {
        Model: attachment,
        Delete
    };

    function Delete() {
        const id = attachment.Id(),
            req = client.Delete();
        req.then(() => {
            data.TopicCardTemplateAttachments.update(req.Date, [], [id]);
        });
    }
}
export type TopicCardTemplateAttachment = ReturnType<typeof TopicCardTemplateAttachment>;


// WEBPACK FOOTER //
// ./apps/portcall/topiccardtemplates/model.ts